<template>
    <b-modal v-model="showModal" size="lg" title="Case Category Permissions" @hidden="close">
        <b-overlay :show="loading" rounded>
            <div v-if="categories.length === 0" class="text-muted">
                <p>No categories defined. Please configure case categories in Configuration first.</p>
            </div>

            <div v-else>
                <p class="mb-3">
                    Define which teams and/or users can view cases in each category.
                    If no permissions are defined for a category, it will be visible to all users with the "View Cases" permission.
                </p>

                <div v-for="category in categories" :key="category.name" class="category-permissions mb-4 p-3 border rounded">
                    <h5 class="mb-3">{{ category.name }}</h5>

                    <b-form-group>
                        <multiselect
                            v-model="permissions[category.name]"
                            :options="watchersOptions"
                            :multiple="true"
                            group-values="options"
                            group-label="type"
                            label="name"
                            track-by="id"
                            placeholder="Select teams or users..."
                        >
                            <template #option="data">
                                <i v-if="data.option.isTeam" class="fa fa-users"></i>
                                <i v-else class="fa fa-user"></i>
                                {{ data.option.$groupLabel }}
                                {{ data.option.name }}
                            </template>

                            <template #tag="data">
                                <b-badge v-if="data.option.isTeam" variant="primary" class="mr-1 my-1 p-2">
                                    <i class="fa fa-users"></i>
                                    {{ data.option.name }}
                                    <i class="fa fa-times ml-2 clickable" @click="data.remove(data.option)"></i>
                                </b-badge>
                                <b-badge v-else variant="info" class="mr-1 my-1 p-2">
                                    <i class="fa fa-user"></i>
                                    {{ data.option.name }}
                                    <i class="fa fa-times ml-2 clickable" @click="data.remove(data.option)"></i>
                                </b-badge>
                            </template>
                        </multiselect>
                    </b-form-group>
                </div>
            </div>
        </b-overlay>

        <template #modal-footer>
            <b-button variant="secondary" @click="close">Cancel</b-button>
            <b-button variant="success" @click="save" :disabled="loading || categories.length === 0">Save</b-button>
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Multiselect from 'vue-multiselect';
import { CaseCategory } from '@/models/Configuration';
import { CaseCategoryPermission } from '@/models/Case';
import { Team } from '@/models/Team';
import { User } from '@/models/User';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

interface WatcherOption {
    id: string | number;
    name: string;
    isTeam?: boolean;
}

@Component({
    name: "CaseCategoryPermissionsModal",
    components: {
        Multiselect
    }
})
export default class CaseCategoryPermissionsModal extends Vue {
    @Prop({ type: Boolean, default: false }) show!: boolean;

    protected showModal = false;
    protected loading = false;
    protected categories: CaseCategory[] = [];
    protected permissions: Record<string, WatcherOption[]> = {};
    protected teams: Team[] = [];
    protected users: User[] = [];

    @Watch('show', { immediate: true })
    onShowChange(val: boolean) {
        this.showModal = val;
        if (val) {
            this.loadData();
        }
    }

    get watchersOptions(): any[] {
        return [
            {
                type: 'Teams',
                options: this.teams.map(t => ({
                    id: t.id,
                    name: t.name,
                    isTeam: true
                }))
            },
            {
                type: 'Users',
                options: this.users.map(u => ({
                    id: u.userName,
                    name: u.displayName || u.userName,
                    isTeam: false
                }))
            }
        ];
    }

    async loadData() {
        this.loading = true;
        try {
            const [categoriesResponse, permissionsResponse, teamsResponse, usersResponse] = await Promise.all([
                this.$http.get<CaseCategory[]>("/Case/categories"),
                this.$http.get<CaseCategoryPermission[]>("/Case/category-permissions"),
                this.$http.get<Team[]>("/user/teams"),
                this.$http.get<User[]>("/user")
            ]);

            this.categories = categoriesResponse.data;
            this.teams = teamsResponse.data;
            this.users = usersResponse.data;

            // Convert permissions to WatcherOption format and organize by category name
            this.permissions = {};
            for (const category of this.categories) {
                const categoryPerms = permissionsResponse.data.filter(
                    p => p.categoryName === category.name
                );

                // Convert CaseCategoryPermission[] to WatcherOption[]
                this.$set(this.permissions, category.name, categoryPerms.map(p => ({
                    id: p.teamId ? p.teamId : p.userName!,
                    name: p.teamId ? p.teamName! : (this.users.find(u => u.userName === p.userName)?.displayName || p.userName!),
                    isTeam: !!p.teamId
                })));
            }
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        } finally {
            this.loading = false;
        }
    }

    async save() {
        this.loading = true;
        try {
            // Convert WatcherOption[] back to CaseCategoryPermission[] format for the API
            const permissionsToSave: Record<string, CaseCategoryPermission[]> = {};

            for (const categoryName of Object.keys(this.permissions)) {
                permissionsToSave[categoryName] = this.permissions[categoryName].map(w => ({
                    categoryName,
                    teamId: w.isTeam ? w.id as number : undefined,
                    userName: !w.isTeam ? w.id as string : undefined,
                    teamName: w.isTeam ? w.name : undefined
                }));
            }

            // Send permissions to backend, grouped by category
            await this.$http.post("/Case/category-permissions", permissionsToSave);
            this.$notification(NotificationOptions.success("Case category permissions saved successfully"));
            this.close();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        } finally {
            this.loading = false;
        }
    }

    close() {
        this.showModal = false;
        this.$emit('update:show', false);
        this.$emit('close');
    }
}
</script>

<style scoped lang="scss">
@import "vue-multiselect/dist/vue-multiselect.min.css";

.category-permissions {
    background-color: #f8f9fa;
}

.clickable {
    cursor: pointer;
    &:hover {
        opacity: 0.7;
    }
}
</style>
